import { secureAuthUtils } from './secureAuth.js';

class ApiClient {
  constructor() {
    // Point to the 2W billing backend admin API
    // Vite uses import.meta.env for environment variables
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/admin';
    
    // Request deduplication cache
    this.pendingRequests = new Map();
    
    // Token will be loaded on first request (lazy loading)
    this.token = null;
    this.tokenPromise = null;
  }

  async ensureToken() {
    if (this.token) {
      // Validate existing token is still valid
      try {
        // Check if token is expired or malformed
        if (typeof this.token !== 'string' || this.token.length < 10) {
          this.token = null;
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('ApiClient: Invalid token format, clearing:', error);
        }
        this.token = null;
      }
    }
    
    if (!this.token && !this.tokenPromise) {
      this.tokenPromise = (async () => {
        try {
          const token = await secureAuthUtils.getToken();
          if (!token) {
            // Token doesn't exist or is expired - this is expected, not an error
            return null;
          }
          if (typeof token !== 'string') {
            throw new Error('Invalid token format received from secure auth');
          }
          return token;
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('ApiClient: Failed to get token:', error);
          }
          this.tokenPromise = null;
          throw error;
        }
      })();
    }
    
    if (this.tokenPromise) {
      this.token = await this.tokenPromise;
      this.tokenPromise = null;
    }
    
    // Don't throw error for missing token - some requests (like login) don't need authentication
    return this.token;
  }

  async setToken(token, userData) {
    this.token = token;
    // Use secure storage instead of localStorage
    await secureAuthUtils.setToken(token, userData);
  }

  async clearToken() {
    this.token = null;
    // Clear secure session
    secureAuthUtils.clearSession();
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const maxRetries = options.retry === false ? 1 : 3;
    const retryDelay = options.retryDelay || 1000;
    const deduplicationKey = this.getDeduplicationKey(endpoint, options);
    let lastError;

    // Request deduplication for GET requests (only if not explicitly disabled)
    if ((!options.method || options.method === 'GET') && options.deduplicate !== false) {
      const existingRequest = this.pendingRequests.get(deduplicationKey);
      if (existingRequest) {
        return existingRequest;
      }
      // Strip signal from dedup'd requests so one caller's abort doesn't kill shared promise
      const dedupOptions = { ...options, signal: undefined };
      const fullRequestPromise = this._executeRequest(url, endpoint, dedupOptions, deduplicationKey, maxRetries, retryDelay);
      this.pendingRequests.set(deduplicationKey, fullRequestPromise);
      fullRequestPromise.finally(() => this.pendingRequests.delete(deduplicationKey));
      return fullRequestPromise;
    }

    return this._executeRequest(url, endpoint, options, deduplicationKey, maxRetries, retryDelay);
  }

  async _executeRequest(url, endpoint, options, deduplicationKey, maxRetries, retryDelay) {
    let lastError;

    // Create or use existing AbortController
    let abortController = options.abortController;
    
    if (!abortController) {
      abortController = new AbortController();
      
      // If external signal provided, link it to our controller
      if (options.signal) {
        options.signal.addEventListener('abort', () => {
          abortController.abort();
        });
      }
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Ensure we have the latest token from secure storage
        await this.ensureToken();

        // Update activity timestamp for authenticated requests
        if (this.token) {
          secureAuthUtils.updateActivity();
        }

        const config = {
          headers: {
            'Content-Type': 'application/json',
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
            ...options.headers,
          },
          signal: abortController?.signal || options.signal,
          ...options,
        };

        // Add CSRF protection for non-GET requests
        if (options.method && options.method !== 'GET') {
          const csrfToken = secureAuthUtils.getCSRFToken();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }

        // Remove internal options before passing to fetch
        delete config.retry;
        delete config.retryDelay;
        delete config.deduplicate;
        delete config.abortController;

        const response = await fetch(url, config);
        
        // Handle 401 - enhanced authentication error handling
        if (response.status === 401) {
          const authError = response.headers.get('X-Auth-Error');
          
          // Only clear token on actual invalid token, not network issues
          if (authError === 'invalid_token' || authError === 'token_expired') {
            console.warn('Authentication failed, clearing secure session');
            await this.clearToken();
            
            // Emit authentication failure event
            window.dispatchEvent(new CustomEvent('auth_failure', {
              detail: { reason: authError, endpoint: endpoint }
            }));
            
            return Promise.reject(new Error('Authentication failed'));
          }
          
          // For other 401s (e.g., temporary auth service issues), retry
          if (attempt < maxRetries) {
            lastError = new Error('Authentication temporarily unavailable');
            await this.delay(retryDelay * Math.pow(2, attempt - 1));
            continue;
          }
        }

        // Handle other error responses
        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Network error' }));
          const errorMessage = error.error || error.message || 'Request failed';

          // Carry the status and the response body onto the Error. `message` is
          // unchanged, so nothing that reads it is affected — but callers can now see
          // WHY a request failed. Without this, `error.status === 401` guards (already
          // written throughout queries.js) can never match, and a 400's validation
          // detail is thrown away before any screen can show it.
          const build = () => {
            const e = new Error(errorMessage);
            e.status = response.status;
            e.body = error;
            if (Array.isArray(error.validation)) e.validation = error.validation;
            return e;
          };
          
          // Don't retry client errors (400-499) except 401, 429
          if (response.status >= 400 && response.status < 500 && 
              response.status !== 401 && response.status !== 429) {
            throw build();
          }
          
          // Retry server errors and rate limiting
          if (attempt < maxRetries) {
            lastError = build();
            
            // Handle rate limiting with longer delay
            if (response.status === 429) {
              const retryAfter = response.headers.get('Retry-After');
              const delay = retryAfter ? parseInt(retryAfter) * 1000 : retryDelay * 4;
              await this.delay(delay);
            } else {
              // Exponential backoff with jitter for other errors
              const delay = Math.min(
                retryDelay * Math.pow(2, attempt - 1) + Math.random() * 1000,
                10000
              );
              await this.delay(delay);
            }
            continue;
          }
          
          throw build();
        }

        return response.json();
      } catch (error) {
        lastError = error;
        
        // Handle AbortError - don't retry aborted requests
        if (error.name === 'AbortError') {
          throw new Error('Request was cancelled');
        }
        
        // Network errors - always retry
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          if (attempt < maxRetries) {
            console.warn(`Network error on attempt ${attempt}/${maxRetries}, retrying...`);
            await this.delay(retryDelay * Math.pow(2, attempt - 1));
            continue;
          }
          throw new Error('Unable to connect to 2W billing backend. Please check if the service is running.');
        }
        
        // Don't retry non-network errors on last attempt
        if (attempt === maxRetries) {
          throw error;
        }
      }
    }
    
    throw lastError || new Error('Request failed after retries');
  }

  // Helper method for delays
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Generate deduplication key for requests with proper collision prevention
  getDeduplicationKey(endpoint, options) {
    const method = options.method || 'GET';
    const body = options.body || '';
    const queryParams = endpoint.includes('?') ? endpoint.split('?')[1] : '';
    
    // Use proper delimiter and encoding to prevent collisions
    const components = [
      method,
      encodeURIComponent(endpoint),
      encodeURIComponent(queryParams),
      encodeURIComponent(body)
    ];
    
    return components.join('||');
  }

  // Clear all pending requests (useful for cleanup)
  clearPendingRequests() {
    this.pendingRequests.clear();
  }

  // Admin authentication with enhanced security
  async login(email, password) {
    // Generate CSRF token for session
    const csrfToken = secureAuthUtils.generateCSRFToken();
    
    const response = await this.request('/auth/login', {
      method: 'POST',
      retry: false,
      body: JSON.stringify({
        email,
        password,
        csrfToken // Include CSRF token in login request
      }),
    });
    
    if (response.token) {
      // Use secure token storage
      await this.setToken(response.token, response.user);
      
      // Emit successful login event
      window.dispatchEvent(new CustomEvent('auth_success', {
        detail: { user: response.user }
      }));
    }
    
    return response;
  }

  // Brand management
  async getBrands(params = {}) {
    const { signal, ...queryParams } = params;
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null && value !== '')
    ).toString();
    const result = await this.request(`/brands${queryString ? `?${queryString}` : ''}`, { signal });
    return result;
  }

  async getBrand(id) {
    return this.request(`/brands/${id}`);
  }

  async updateBrand(id, data) {
    return this.request(`/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Access request management
  async getAccessRequests(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/access-requests${query ? '?' + query : ''}`);
  }

  /**
   * Everything the admin Dashboard needs beyond /analytics/detailed:
   * connector volume, catalogue, sync errors by type, logistics SLA, access requests.
   */
  async getDashboardSummary(params = {}) {
    const { signal, ...rest } = params;
    const query = new URLSearchParams(rest).toString();
    return this.request(`/dashboard/summary${query ? '?' + query : ''}`, signal ? { signal } : {});
  }

  /** Tracking screen: pipeline, queues, SLA by retailer, failures. */
  async getTrackingSummary(params = {}) {
    const { signal, ...rest } = params;
    const q = new URLSearchParams(rest).toString();
    return this.request(`/tracking/summary${q ? '?' + q : ''}`, signal ? { signal } : {});
  }

  async getTrackingJobs(params = {}) {
    const { signal, ...rest } = params;
    const q = new URLSearchParams(rest).toString();
    return this.request(`/tracking/jobs${q ? '?' + q : ''}`, signal ? { signal } : {});
  }

  /**
   * Inventory Sync screen: the stock import ledger resolved through STO02, and the
   * rejections drawn from it. It no longer carries the sync-job list or the
   * per-connection health rows — Background Jobs -> Sync History reports the jobs.
   */
  async getInventorySyncSummary(params = {}) {
    const { signal, ...rest } = params;
    const q = new URLSearchParams(rest).toString();
    return this.request(`/inventory-sync/summary${q ? '?' + q : ''}`, signal ? { signal } : {});
  }

  /** STO03 error report for one stock import, fetched on demand — it can be large. */
  async getOfferImportErrorReport(importRowId) {
    return this.request(`/inventory-sync/imports/${importRowId}/error-report`);
  }

  /**
   * Run one order through OR23/OR24 on demand. `action` is validate | push | recheck.
   * Resolves with { ok, message } for BOTH outcomes — a retailer's refusal comes back
   * as 200 with ok:false, because it is an answer, not a transport failure.
   */
  async runTrackingAction(orderRowId, action) {
    return this.request(`/tracking/orders/${orderRowId}/${action}`, { method: 'POST', retry: false });
  }

  /** Analytics → Connector Performance: operational counts, funnel, losses, sync health. */
  async getConnectorPerformance(params = {}) {
    const { signal, ...rest } = params;
    const q = new URLSearchParams(rest).toString();
    return this.request(`/analytics/connector-performance${q ? '?' + q : ''}`, signal ? { signal } : {});
  }

  /**
   * Connector sync settings. The response carries the option lists and ceilings, so
   * the screen renders exactly what the server will accept rather than a local copy.
   */
  async getConnectorSettings() {
    return this.request('/connector-settings');
  }

  async updateConnectorSettings(patch) {
    return this.request('/connector-settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  }

  async getAppSettings() {
    return this.request('/app-settings');
  }

  async updateAppSettings(patch) {
    return this.request('/app-settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  }

  async getPendingAccessRequestCount() {
    return this.request('/access-requests/pending-count');
  }

  async approveAccessRequest(id) {
    return this.request(`/access-requests/${id}/approve`, { method: 'POST' });
  }

  async rejectAccessRequest(id, notes) {
    return this.request(`/access-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  // Order source filtering. One call serves both pickers on Order Sources to Bill: the apps
  // observed on this brand's orders, the marketplaces attribution can produce for it, an order
  // count against each, and how many orders carry no app id at all.
  async getBillingScopeOptions(brandId) {
    return this.request(`/brands/${brandId}/billing-scope-options`);
  }

  // Commission overrides
  async createCommissionOverride(brandId, data) {
    return this.request(`/brands/${brandId}/commission-overrides`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCommissionOverride(brandId, overrideId) {
    return this.request(`/brands/${brandId}/commission-overrides/${overrideId}`, {
      method: 'DELETE',
    });
  }

  // Enhanced Shipping fees - Updated for new weight range system
  async createShippingFee(brandId, data) {
    const apiData = {
      shippingMethod: data.shippingMethod,
      weightMin: data.weightMin !== null && data.weightMin !== '' ? parseFloat(data.weightMin) : null,
      weightMax: data.weightMax !== null && data.weightMax !== '' ? parseFloat(data.weightMax) : null,
      fee: parseFloat(data.fee),
      returnFee: data.returnFee && data.returnFee !== '' ? parseFloat(data.returnFee) : null
    };

    return this.request(`/brands/${brandId}/shipping-fees`, {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  async updateShippingFee(brandId, feeId, data) {
    const apiData = {
      shippingMethod: data.shippingMethod,
      weightMin: data.weightMin !== null && data.weightMin !== '' ? parseFloat(data.weightMin) : null,
      weightMax: data.weightMax !== null && data.weightMax !== '' ? parseFloat(data.weightMax) : null,
      fee: parseFloat(data.fee),
      returnFee: data.returnFee && data.returnFee !== '' ? parseFloat(data.returnFee) : null
    };

    return this.request(`/brands/${brandId}/shipping-fees/${feeId}`, {
      method: 'PUT',
      body: JSON.stringify(apiData),
    });
  }

  async validateShippingFeeRule(brandId, data) {
    const apiData = {
      shippingMethod: data.shippingMethod,
      weightMin: data.weightMin !== null && data.weightMin !== '' ? parseFloat(data.weightMin) : null,
      weightMax: data.weightMax !== null && data.weightMax !== '' ? parseFloat(data.weightMax) : null,
      fee: parseFloat(data.fee),
      returnFee: data.returnFee && data.returnFee !== '' ? parseFloat(data.returnFee) : null,
      excludeRuleId: data.excludeRuleId || null
    };

    return this.request(`/brands/${brandId}/shipping-fees/validate`, {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  // NOTE: the old getShippingRules (deprecated ShippingRule model) is gone. The two methods
  // below hit GET /brands/:brandId/shipping-rules, which reads the weight-based
  // ShippingFeeTable and is the only endpoint that returns the RAW rows for a brand -
  // including rows on a shippingMethod or a weight band that
  // GET /brands/:brandId/shipping-pricing filters out of its fixed grid server-side.
  //
  // Response shape (shipping.controller.js:206-236):
  //   { shippingRules: [{ id, shippingMethod, weightMin, weightMax, fee, createdAt,
  //                       updatedAt, isActive, region, carrierType,
  //                       weightRange, feeFormatted }],
  //     pagination: { page, limit, totalCount, totalPages, hasNextPage,
  //                   hasPreviousPage, startIndex, endIndex },
  //     meta: { searchTerm, sortBy, sortDirection, totalShippingMethods } }
  //
  // Called only by getAllShippingFeeRules below, which pages through it. Kept as a separate
  // method because the paging loop needs a single-page primitive.
  async getShippingFeeRules(brandId, params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);
    const qs = query.toString();
    return this.request(`/brands/${brandId}/shipping-rules${qs ? `?${qs}` : ''}`);
  }

  /**
   * Walk every page of GET /brands/:brandId/shipping-rules and return the full row set.
   * The endpoint caps `limit` at 50 (shipping.controller.js:160) and a fully populated
   * brand has more rows than that, so a single page would silently truncate.
   * Returns { shippingRules, totalCount, totalShippingMethods, truncated }.
   */
  async getAllShippingFeeRules(brandId, { maxPages = 20, limit = 50 } = {}) {
    const rows = [];
    let page = 1;
    let totalCount = 0;
    let totalShippingMethods = 0;
    let hasNextPage = true;

    while (hasNextPage && page <= maxPages) {
      const response = await this.getShippingFeeRules(brandId, {
        page,
        limit,
        sortBy: 'shippingMethod',
        sortDirection: 'asc',
      });

      if (Array.isArray(response?.shippingRules)) {
        rows.push(...response.shippingRules);
      }
      totalCount = response?.pagination?.totalCount ?? totalCount;
      totalShippingMethods = response?.meta?.totalShippingMethods ?? totalShippingMethods;
      hasNextPage = Boolean(response?.pagination?.hasNextPage);
      page += 1;
    }

    return {
      shippingRules: rows,
      totalCount,
      totalShippingMethods,
      truncated: hasNextPage, // still more pages left when we hit maxPages
    };
  }

  async deleteShippingFee(brandId, feeId) {
    return this.request(`/brands/${brandId}/shipping-fees/${feeId}`, {
      method: 'DELETE',
    });
  }

  async resetShippingFeesToDefault(brandId) {
    return this.request(`/brands/${brandId}/shipping-fees/reset`, {
      method: 'DELETE',
    });
  }

  // Return fee management
  async getDefaultReturnFee(brandId) {
    return this.request(`/brands/${brandId}/return-fees`);
  }

  async updateDefaultReturnFee(brandId, returnFee) {
    return this.request(`/brands/${brandId}/return-fees`, {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async deleteDefaultReturnFee(brandId) {
    return this.request(`/brands/${brandId}/return-fees`, {
      method: 'DELETE',
    });
  }

  async getMarketplaceReturnFee(brandId, marketplace) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`);
  }

  async createMarketplaceReturnFee(brandId, marketplace, returnFee) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'POST',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async updateMarketplaceReturnFee(brandId, marketplace, returnFee) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async deleteMarketplaceReturnFee(brandId, marketplace) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'DELETE',
    });
  }

  async resetMarketplaceReturnFeesToDefault(brandId) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/reset-all`, {
      method: 'DELETE',
    });
  }

  // Global return fee defaults management
  async getGlobalReturnFeeDefaults() {
    return this.request('/return-fees/defaults');
  }

  async updateGlobalReturnFeeDefault(returnFee) {
    return this.request('/return-fees/defaults', {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async createGlobalMarketplaceReturnFee(marketplace, returnFee) {
    return this.request(`/return-fees/defaults/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'POST',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async updateGlobalMarketplaceReturnFee(marketplace, returnFee) {
    return this.request(`/return-fees/defaults/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async deleteGlobalMarketplaceReturnFee(marketplace) {
    return this.request(`/return-fees/defaults/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'DELETE',
    });
  }

  // Sync coverage tracking
  async getSyncCoverage(brandId) {
    return this.request(`/orders/${brandId}/sync/coverage`);
  }

  // Activity logging
  async getBrandActivity(brandId) {
    try {
      return this.request(`/brands/${brandId}/activity`);
    } catch (error) {
      // If activity endpoint doesn't exist yet, return empty array
      console.warn('Activity logging not implemented yet:', error.message);
      return [];
    }
  }

  // Brand-specific analytics
  async getBrandAnalytics(brandId, params = {}) {
    const queryParams = {
      brand: brandId,
      ...params
    };
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/detailed?${queryString}`);
  }

  /**
   * Fulfilment and shipping figures for one brand.
   *
   * A separate endpoint from getBrandAnalytics rather than more fields on it: that one's
   * SQL bounds on fulfillmentDate and so cannot see an UNFULFILLED order at all, which is
   * most of what this asks about.
   *
   * Takes the same params object as getBrandAnalytics — days, or startDate/endDate.
   */
  async getBrandLogistics(brandId, params = {}) {
    const queryParams = { brand: brandId, ...params };
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/logistics?${queryString}`);
  }

  // ==========================================
  // ANALYTICS ENDPOINTS - Enhanced for Admin Dashboard
  // ==========================================

  /**
   * Get system-wide analytics overview
   * @returns {Promise<Object>} Overview metrics across all brands
   */
  async getAnalyticsOverview(options = {}) {
    const { signal } = options;
    return this.request('/analytics/overview', { signal });
  }

  /**
   * Get detailed analytics with filtering options
   * @param {Object} params - Filter parameters
   * @param {string} [params.brand] - Brand ID or 'all'
   * @param {number} [params.days=30] - Number of days to analyze
   * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
   * @param {string} [params.endDate] - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} Detailed analytics data
   */
  async getDetailedAnalytics(params = {}) {
    const { signal, ...queryParams } = params;
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/detailed?${queryString}`, { signal });
  }

  /**
   * Get brand-specific performance reports
   * @param {Object} params - Filter parameters
   * @param {string} [params.brand] - Brand ID or 'all'
   * @param {number} [params.days=30] - Number of days to analyze
   * @returns {Promise<Array>} Array of brand performance data
   */
  async getBrandReports(params = {}) {
    const { signal, ...queryParams } = params;
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/brand-reports?${queryString}`, { signal });
  }

  /**
   * Get marketplace analytics and breakdown
   * @param {Object} params - Filter parameters
   * @param {string} [params.brand] - Brand ID or 'all'
   * @param {number} [params.days=30] - Number of days to analyze
   * @returns {Promise<Array>} Marketplace performance data
   */
  async getMarketplaceAnalytics(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/marketplace?${queryString}`);
  }

  /**
   * Get cross-brand commission summary
   * @param {Object} params - Filter parameters
   * @returns {Promise<Object>} Commission summary across all brands
   */
  async getCommissionSummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/commission-summary?${queryString}`);
  }

  /**
   * Get trending data for dashboard charts
   * @param {Object} params - Parameters
   * @param {string} params.metric - Metric to track (revenue, commission, orders)
   * @param {string} params.period - Time period (week, month, quarter, year)
   * @returns {Promise<Array>} Trending data points
   */
  async getTrendingData(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/trends?${queryString}`);
  }

  // Billing operations
  async getBillingReport(brandId, year, month, options = {}) {
    const queryString = new URLSearchParams(options).toString();
    return this.request(`/billing/${brandId}/${year}/${month}?${queryString}`);
  }

  async generateBillingReport(brandId, year, month) {
    const response = await this.request(`/billing/${brandId}/${year}/${month}/generate`, {
      method: 'POST',
    });

    // If we got a cached report (200), return it directly
    if (response.cached || response.id) {
      return response;
    }

    // If async generation started (202), poll for completion
    if (response.status === 'generating' && response.jobId) {
      return this.pollBillingJobStatus(response.jobId, brandId, year, month);
    }

    return response;
  }

  async getBillingJobStatus(jobId) {
    return this.request(`/billing/job/${jobId}/status`);
  }

  async pollBillingJobStatus(jobId, brandId, year, month, maxAttempts = 120, intervalMs = 2000) {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const status = await this.getBillingJobStatus(jobId);

      if (status.status === 'completed') {
        // Return the report from the job or fetch it
        if (status.report) {
          return status.report;
        }
        // Fetch the latest report
        return this.getBillingReport(brandId, year, month);
      }

      if (status.status === 'failed') {
        throw new Error(status.failedReason || 'Report generation failed');
      }

      if (status.status === 'not_found') {
        throw new Error('Job not found');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    }

    throw new Error('Report generation timed out. Please try again.');
  }

  // ==========================================
  // ORDER MANAGEMENT - Enhanced for Admin Dashboard
  // ==========================================

  /**
   * Get orders for a specific brand with pagination and filtering
   * @param {string} brandId - Brand ID
   * @param {Object} options - Query options
   * @param {number} [options.page=1] - Page number
   * @param {number} [options.limit=50] - Items per page
   * @param {string} [options.search] - Search term
   * @param {string} [options.sortBy] - Sort field
   * @param {string} [options.sortDirection] - Sort direction (asc/desc)
   * @param {Array} [options.statusFilter] - Status filters
   * @param {string} [options.startDate] - Start date filter
   * @param {string} [options.endDate] - End date filter
   * @returns {Promise<Object>} Paginated orders data
   */
  async getBrandOrders(brandId, options = {}) {
    const queryString = new URLSearchParams(
      Object.entries(options).filter(([, value]) => value != null)
    ).toString();
    const url = `/orders/${brandId}?${queryString}`;
    return this.request(url);
  }

  /**
   * Get list of all distinct marketplaces for filter dropdowns
   * @param {Object} options - Query options
   * @param {string} [options.brandId] - Optional brand ID to filter marketplaces
   * @returns {Promise<Object>} List of marketplaces
   */
  async getMarketplacesList(options = {}) {
    try {
      const { signal, brandId, ...queryParams } = options;
      const params = { ...queryParams };
      if (brandId && brandId !== 'all') {
        params.brandId = brandId;
      }
      const queryString = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value != null)
      ).toString();
      const url = `/marketplaces/list${queryString ? `?${queryString}` : ''}`;
      return this.request(url, { signal });
    } catch (error) {
      // Return empty list on error
      console.error('Failed to fetch marketplaces list:', error);
      return { marketplaces: [], count: 0 };
    }
  }

  /**
   * Get orders across all brands (admin view)
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Cross-brand orders data
   */
  async getAllBrandOrders(options = {}) {
    try {
      const { signal, ...queryParams } = options;
      const queryString = new URLSearchParams(
        Object.entries(queryParams).filter(([, value]) => value != null)
      ).toString();
      const url = `/orders?${queryString}`;
      return this.request(url, { signal });
    } catch (error) {
      // Don't return fallback data for cancelled requests
      if (error?.message === 'Request was cancelled') {
        throw error;
      }
      console.error('Error fetching all brand orders:', error);
      return { orders: [], pagination: { page: 1, limit: options.limit || 50, totalOrders: 0, totalPages: 0 } };
    }
  }

  /**
   * Sync orders for a brand
   * @param {string} brandId - Brand ID
   * @param {Object} options - Sync options
   * @param {string} [options.startDate] - Start date for historical sync (YYYY-MM-DD)
   * @param {string} [options.endDate] - End date for historical sync (YYYY-MM-DD)
   * @param {boolean} [options.forceSync] - Force sync even if one is already running
   * @param {number} [options.maxMonths] - Maximum months to sync (default: 60)
   * @returns {Promise<Object>} Sync job details
   */
  async syncOrders(brandId, options = {}) {
    return this.request(`/orders/${brandId}/sync`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  /**
   * Get sync status for a brand
   * @param {string} brandId - Brand ID
   * @returns {Promise<Object>} Sync status details
   */
  async getSyncStatus(brandId) {
    return this.request(`/orders/${brandId}/sync/status`);
  }


  /**
   * Get marketplace breakdown for orders
   * @param {Object} params - Parameters
   * @param {string} [params.brandId] - Specific brand or 'all'
   * @param {string} [params.period] - Time period
   * @returns {Promise<Array>} Marketplace breakdown data
   */
  async getOrderMarketplaceBreakdown(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/orders/marketplace-breakdown?${queryString}`);
  }


  // User management (for future implementation)
  async getUsers() {
    return this.request('/users');
  }

  async createUser(data) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // ==========================================
  // MARKETPLACE PATTERNS API - Step 1.2 Implementation
  // ==========================================

  /**
   * Get marketplace patterns with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=50] - Items per page
   * @param {string} [params.marketplace] - Filter by marketplace name
   * @param {string} [params.isActive] - Filter by active status
   * @param {string} [params.search] - Search term
   * @returns {Promise<Object>} Paginated patterns data
   */
  async getMarketplacePatterns(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null && value !== '')
    ).toString();
    return this.request(`/marketplace-patterns${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Create a new marketplace pattern
   * @param {Object} patternData - Pattern configuration
   * @param {string} patternData.marketplaceName - Marketplace name
   * @param {string} [patternData.displayName] - Optional display name
   * @param {string} patternData.patternType - Pattern type (EXACT, CONTAINS, etc.)
   * @param {string} patternData.patternValue - Pattern value/expression
   * @param {number} [patternData.priority=1] - Priority (1-10)
   * @param {boolean} [patternData.isActive=true] - Active status
   * @param {boolean} [patternData.isCaseSensitive=false] - Case sensitivity
   * @param {string} [patternData.category='primary'] - Pattern category
   * @param {string} [patternData.description] - Description/notes
   * @returns {Promise<Object>} Created pattern
   */
  async createMarketplacePattern(patternData) {
    return this.request('/marketplace-patterns', {
      method: 'POST',
      body: JSON.stringify(patternData)
    });
  }

  /**
   * Update an existing marketplace pattern
   * @param {string} id - Pattern ID
   * @param {Object} patternData - Updated pattern data
   * @returns {Promise<Object>} Updated pattern
   */
  async updateMarketplacePattern(id, patternData) {
    return this.request(`/marketplace-patterns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patternData)
    });
  }

  /**
   * Delete a marketplace pattern
   * @param {string} id - Pattern ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteMarketplacePattern(id) {
    return this.request(`/marketplace-patterns/${id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Test a marketplace pattern against sample tags
   * @param {Object} testData - Test configuration
   * @param {string} testData.patternValue - Pattern to test
   * @param {string} testData.patternType - Pattern type
   * @param {Array<string>} [testData.testTags] - Tags to test against
   * @returns {Promise<Object>} Test results with validation and matches
   */
  async testMarketplacePattern(testData) {
    return this.request('/marketplace-patterns/test', {
      method: 'POST',
      body: JSON.stringify(testData)
    });
  }

  /**
   * Get marketplace pattern usage analytics
   * @param {Object} params - Analytics parameters
   * @param {string} [params.brandId] - Specific brand or 'all'
   * @param {number} [params.days=30] - Analysis period in days
   * @returns {Promise<Object>} Analytics data with usage stats and distribution
   */
  async getMarketplacePatternsAnalytics(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/marketplace-patterns/analytics${queryString ? `?${queryString}` : ''}`);
  }

  // ==========================================
  // PER-BRAND MARKETPLACE PATTERN OVERRIDES
  //
  // The escape hatch for the mis-attribution trap. Global patterns above apply to every
  // brand; an override changes detection for one brand only — either adding a pattern the
  // global set lacks, or opting the brand out of a global pattern that sends its orders to
  // the wrong retailer (and therefore bills them at the wrong commission rate).
  // ==========================================

  /**
   * Enum values and limits for the override form, served from the same server constants the
   * validator uses so the form cannot offer a value the API will reject.
   * @returns {Promise<{patternTypes: string[], overrideActions: string[], priorityRange: {min: number, max: number}}>}
   */
  async getBrandMarketplaceOverrideOptions() {
    return this.request('/brand-marketplace-overrides/options');
  }

  /**
   * List per-brand marketplace pattern overrides
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=50] - Items per page
   * @param {string} [params.brandId] - Filter to one brand ('all' or omitted for every brand)
   * @param {string} [params.marketplace] - Filter by marketplace name (case-insensitive contains)
   * @param {string} [params.isActive] - Filter by active status ('true' / 'false')
   * @param {string} [params.overrideAction] - Filter by ADD, EXTEND, REPLACE, DISABLE or MODIFY
   * @returns {Promise<Object>} Paginated overrides data
   */
  async getBrandMarketplaceOverrides(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null && value !== '')
    ).toString();
    return this.request(`/brand-marketplace-overrides${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Create a per-brand marketplace pattern override
   * @param {Object} overrideData - Override configuration
   * @param {string} overrideData.brandId - Brand the override applies to (must exist)
   * @param {string} overrideData.overrideAction - ADD, EXTEND, REPLACE, DISABLE or MODIFY
   * @param {string} [overrideData.globalPatternId] - Global pattern being acted on. Required
   *   for EXTEND, REPLACE, DISABLE and MODIFY; omit for ADD to introduce a new retailer.
   * @param {string} [overrideData.marketplaceName] - Retailer name. Derived from the global
   *   pattern when globalPatternId is given.
   * @param {string} [overrideData.displayName] - Optional display name
   * @param {string} [overrideData.patternType] - EXACT, CONTAINS, STARTS_WITH, ENDS_WITH,
   *   REGEX or WORD_BOUNDARY. Must be omitted for DISABLE — the server derives a
   *   never-matching pattern instead.
   * @param {string} [overrideData.patternValue] - Pattern value. Must be omitted for DISABLE.
   * @param {number} [overrideData.priority=1] - Priority (1-10, rejected outside that range)
   * @param {boolean} [overrideData.isActive=true] - Active status
   * @param {boolean} [overrideData.isCaseSensitive=false] - Case sensitivity
   * @param {string} [overrideData.reason] - Why this override exists. Required for DISABLE.
   * @param {boolean} [overrideData.isTemporary] - Whether the override expires
   * @param {string} [overrideData.expiresAt] - ISO date; must be in the future
   * @returns {Promise<Object>} Created override
   */
  async createBrandMarketplaceOverride(overrideData) {
    return this.request('/brand-marketplace-overrides', {
      method: 'POST',
      body: JSON.stringify(overrideData)
    });
  }

  /**
   * Update a per-brand marketplace pattern override. Only the fields sent are changed.
   * brandId is immutable — delete and re-create to move an override to another brand.
   * @param {string} id - Override ID
   * @param {Object} overrideData - Fields to change
   * @returns {Promise<Object>} Updated override
   */
  async updateBrandMarketplaceOverride(id, overrideData) {
    return this.request(`/brand-marketplace-overrides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(overrideData)
    });
  }

  /**
   * Delete a per-brand marketplace pattern override, returning the brand to the global patterns
   * @param {string} id - Override ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteBrandMarketplaceOverride(id) {
    return this.request(`/brand-marketplace-overrides/${id}`, {
      method: 'DELETE'
    });
  }

  // ==========================================
  // CONNECTOR (MIRAKL) METHODS
  // ==========================================

  async getConnectorBrands() {
    return this.request('/connector/brands');
  }

  async getConnectorConfig(brandId) {
    return this.request(`/connector/brands/${brandId}/config`);
  }

  async updateConnectorConfig(brandId, data) {
    return this.request(`/connector/brands/${brandId}/config`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async triggerConnectorSync(brandId) {
    return this.request(`/connector/brands/${brandId}/sync`, {
      method: 'POST',
    });
  }

  async triggerTrackingPush(brandId) {
    return this.request(`/connector/brands/${brandId}/tracking-push`, {
      method: 'POST',
    });
  }

  async getConnectorOrders(brandId, params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null)
    ).toString();
    return this.request(`/connector/brands/${brandId}/orders${queryString ? `?${queryString}` : ''}`);
  }

  async getConnectorHistory(brandId) {
    return this.request(`/connector/brands/${brandId}/history`);
  }

  // The same record without a brand scope — every account's runs, optionally one retailer's.
  // Four of the six run types record no brand, so they appear here and on no brand page.
  async getSyncHistory({ marketplaceId } = {}) {
    const qs = marketplaceId ? `?marketplaceId=${encodeURIComponent(marketplaceId)}` : '';
    return this.request(`/connector/history${qs}`);
  }

  // BOTH halves of a brand's background work in one list — the connector's runs and billing's.
  //
  // getConnectorHistory above returns the connector half only, which is why a brand page could
  // show an inventory sync but never the invoice build that followed it: billing records its
  // runs in its own table, and the console read one of the two. The merged route returns a
  // single row shape carrying `source`, so the caller does not have to know which side wrote a
  // row — but it must still SAY which side, or the two get read as one system.
  //
  // `scope` picks the permanent record ('history') or the work still outstanding ('queue').
  // Same row shape either way, which is what lets the history table and the queue table share
  // one normaliser.
  // Every brand's runs, both systems — what the Background Jobs pages show. Separate from
  // getBrandRuns because "no brand" is a different question, not a missing argument.
  // `marketplaceId` is forwarded, and was not. The global Sync History page passes it from
  // its Retailer select and the route has always supported it, so the filter sat on screen
  // doing nothing — it narrowed no rows and reported no error. Note the route answers a
  // retailer filter by excluding billing rows entirely, which is deliberate: billing never
  // talks to a retailer, so no billing run belongs to one account.
  async getRuns({ scope = 'history', limit, marketplaceId } = {}) {
    const qs = new URLSearchParams(
      Object.entries({ scope, limit, marketplaceId }).filter(([, v]) => v !== '' && v != null),
    ).toString();
    return this.request(`/connector/runs${qs ? `?${qs}` : ''}`);
  }

  async getBrandRuns(brandId, { scope = 'history', limit } = {}) {
    const qs = new URLSearchParams(
      Object.entries({ scope, limit })
        .filter(([, v]) => v !== '' && v !== null && v !== undefined),
    ).toString();
    return this.request(`/connector/brands/${brandId}/runs${qs ? `?${qs}` : ''}`);
  }

  // Per-account manual runs.
  //
  // READS carry `manual: true` server-side, which bypasses their Sync Settings toggle: a
  // paused schedule should not stop an admin looking at a retailer. WRITES do not — every
  // write route refuses with a 409 naming the setting while its operation is off, and the
  // worker's write gates take no manual escape either. This comment previously said the
  // bypass applied to all of them, which stopped being true when writes were made strict.
  async fetchAccountOrders(marketplaceId) {
    return this.request(`/connector/marketplaces/${marketplaceId}/fetch-orders`, { method: 'POST' });
  }

  async fetchAccountOffers(marketplaceId) {
    return this.request(`/connector/marketplaces/${marketplaceId}/fetch-offers`, { method: 'POST' });
  }

  // Queue stock pushes for one retailer, then flush them. `dryRun` reports what WOULD be
  // queued and how old the Shopify figures are, without writing — that is what the
  // confirmation is built from, so it can name a real count.
  async queueRetailerInventoryPush(marketplaceId, { shopSkus = null, onlyDiffering = false, dryRun = false } = {}) {
    return this.request(`/connector/marketplaces/${marketplaceId}/queue-inventory-push`, {
      method: 'POST',
      body: JSON.stringify({ shopSkus, onlyDiffering, dryRun }),
    });
  }

  async pushAccountInventory(marketplaceId) {
    return this.request(`/connector/marketplaces/${marketplaceId}/push-inventory`, { method: 'POST' });
  }

  async fetchAccountInventories(marketplaceId) {
    return this.request(`/connector/marketplaces/${marketplaceId}/fetch-inventories`, { method: 'POST' });
  }

  // Accept ONE retailer order on Mirakl. Can also refuse it — acceptance means "decide" — so the
  // response carries a `decision` rather than only success.
  async acceptMiraklOrder(orderId) {
    return this.request(`/connector/orders/${orderId}/accept`, { method: 'POST' });
  }

  // Push tracking for ONE order. Lives under /tracking, not /connector.
  async pushOrderTracking(orderId) {
    return this.request(`/tracking/orders/${orderId}/push`, { method: 'POST' });
  }

  async getSkuMapStats() {
    return this.request('/connector/sku-map/stats');
  }

  // Brand-scoped counts, for a brand's Retail Connect tab. The unscoped call above covers every
  // brand on every retailer account and belongs on the Retailer Accounts screen.
  async getBrandSkuMapStats(brandId) {
    return this.request(`/connector/brands/${brandId}/sku-map/stats`);
  }

  async refreshSkuMap() {
    return this.request('/connector/sku-map/refresh', { method: 'POST' });
  }

  // The SKU comparison rows. `params` may carry marketplaceId, search, status, page, limit,
  // sortBy and sortDirection; empty values are dropped so the query string only says what was
  // actually asked for.
  async getBrandSkuMapRows(brandId, params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
    ).toString();
    return this.request(`/connector/brands/${brandId}/sku-map/rows${qs ? `?${qs}` : ''}`);
  }

  // The same table across every brand. Takes brandId as a FILTER rather than a scope.
  async getSkuMapRows(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
    ).toString();
    return this.request(`/connector/sku-map/rows${qs ? `?${qs}` : ''}`);
  }

  // Re-read ONE SKU from both sides. Reads only — no write reaches the retailer — so it is
  // safe with every write toggle off, and the response says what was re-read.
  async refreshOneSku({ sku, brandId = null, marketplaceId = null }) {
    return this.request('/connector/sku-map/refresh-sku', {
      method: 'POST',
      body: JSON.stringify({ sku, brandId, marketplaceId }),
    });
  }

  // The jobs behind the queue counts — failed, running, waiting, scheduled, recently skipped
  // and recently done. `params` may carry state, type, brandId, marketplaceId and limit.
  async getQueueJobs(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
    ).toString();
    return this.request(`/connector/queue/jobs${qs ? `?${qs}` : ''}`);
  }

  // Push one Mirakl order to Shopify. Runs inline, so the response IS the outcome.
  // Create the attribution pattern for a connection's tag prefix, so its orders stop billing
  // as "Others".
  // Create the attribution pattern for a retailer ACCOUNT's prefix.
  async createAccountAttribution(marketplaceId) {
    return this.request(`/connector/marketplaces/${marketplaceId}/attribution`, { method: 'POST' });
  }

  async createConnectionAttribution(brandId, marketplaceId) {
    return this.request(`/connector/brands/${brandId}/marketplaces/${marketplaceId}/attribution`, {
      method: 'POST',
    });
  }

  async syncSingleMiraklOrder(orderId) {
    return this.request(`/connector/orders/${orderId}/sync`, { method: 'POST' });
  }

  // getConnectorQueueFailures and getConnectorQueueMetrics removed with the Queue card's
  // counts-and-a-modal. getQueueJobs above returns the failed jobs WITH their reasons and the
  // same counts in one call, so the two older calls were a second and third way to ask the
  // same question — and two calls that can disagree with each other for a poll interval.
  // Their routes remain (/connector/queue/failures, /connector/queue/metrics) and are still
  // the place to look when comparing what the table shows against the queue itself.

  async getGlobalConnectorOrders(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null)
    ).toString();
    return this.request(`/connector/orders${queryString ? `?${queryString}` : ''}`);
  }

  // Marketplace account management
  async getConnectorMarketplaces() {
    return this.request('/connector/marketplaces');
  }

  // How the most recent run of each type actually went, so a table can warn that the rows it
  // is showing came from a run that failed or fell short. Both filters are optional and
  // independent; neither given means the newest run of each type across everything.
  async getRunHealth({ marketplaceId, brandId } = {}) {
    const qs = new URLSearchParams(
      Object.entries({ marketplaceId, brandId }).filter(([, v]) => v),
    ).toString();
    return this.request(`/connector/run-health${qs ? `?${qs}` : ''}`);
  }

  // Asks a set of credentials who they belong to before anything is written, so the account
  // can be created already carrying the retailer's own platform name.
  async probeConnectorMarketplace({ apiUrl, apiKey }) {
    return this.request('/connector/marketplaces/probe', {
      method: 'POST',
      body: JSON.stringify({ apiUrl, apiKey }),
    });
  }

  async createConnectorMarketplace(data) {
    return this.request('/connector/marketplaces', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConnectorMarketplace(id, data) {
    return this.request(`/connector/marketplaces/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deactivateConnectorMarketplace(id) {
    return this.request(`/connector/marketplaces/${id}/deactivate`, {
      method: 'PUT',
    });
  }

  async deleteConnectorMarketplace(id) {
    return this.request(`/connector/marketplaces/${id}`, {
      method: 'DELETE',
    });
  }

  async verifyConnectorMarketplace(id) {
    return this.request(`/connector/marketplaces/${id}/verify`, {
      method: 'POST',
    });
  }

  async getConnectorMarketplaceStats(id) {
    return this.request(`/connector/marketplaces/${id}/stats`);
  }

  // What ONE retailer lists, from our copy of its catalogue. Unlike the SKU map endpoint
  // this needs no brand connection — it reports every offer we hold for the account.
  /** Retailer -> brand -> is it connected, across every active retailer. */
  async getBrandConnections() {
    return this.request('/connector/brand-connections');
  }

  async getRetailerCatalogue(marketplaceId, params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
    ).toString();
    return this.request(`/connector/marketplaces/${marketplaceId}/catalogue${qs ? `?${qs}` : ''}`);
  }

  // Every order this retailer sent, as STORED — before attribution split it into per-brand
  // slices. Needs no brand connection, which is the point: an order that could not be
  // attributed used never to be written at all, so a misconfigured brand name and a retailer
  // with no orders looked identical. `state` accepts 'unresolved' as well as each individual
  // attribution state; the service whitelists it, so an unknown value simply returns all.
  // Pages SERVER-side, so `limit` is the row count that goes out rather than a local slice.
  async getStoredRetailerOrders(marketplaceId, params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
    ).toString();
    return this.request(`/connector/marketplaces/${marketplaceId}/raw-orders${qs ? `?${qs}` : ''}`);
  }

  /**
   * One stored retailer order in full, for the row detail.
   *
   * A separate call rather than a field on the list, because the payload is the whole OR11
   * object per order and the list pages up to 200 of them. encodeURIComponent because a
   * Mirakl order id is not a slug — the real ones carry "-A" and "-B" suffixes.
   */
  async getStoredRetailerOrderDetail(marketplaceId, miraklOrderId) {
    return this.request(
      `/connector/marketplaces/${marketplaceId}/raw-orders/${encodeURIComponent(miraklOrderId)}`,
    );
  }

  // Re-run attribution over the orders already stored for one retailer. Reads our own
  // database only — nothing goes to the retailer or to Shopify — so it is safe to press as
  // often as you like and is the recovery path after correcting a brand name.
  //
  // `onlyPending` is left unset by default on purpose: the route treats absent as false and
  // re-attributes EVERYTHING, which is what a person who has just fixed a connection means.
  // A pending-only pass would skip the very rows an earlier pass marked UNATTRIBUTED.
  async attributeStoredRetailerOrders(marketplaceId, { onlyPending } = {}) {
    return this.request(`/connector/marketplaces/${marketplaceId}/attribute-orders`, {
      method: 'POST',
      body: JSON.stringify(onlyPending === undefined ? {} : { onlyPending }),
    });
  }

  // Brand-marketplace connections
  async getBrandMarketplaces(brandId) {
    return this.request(`/connector/brands/${brandId}/marketplaces`);
  }

  async connectBrandMarketplace(brandId, marketplaceId, data = {}) {
    return this.request(`/connector/brands/${brandId}/marketplaces/${marketplaceId}/connect`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBrandMarketplaceConfig(brandId, marketplaceId, data) {
    return this.request(`/connector/brands/${brandId}/marketplaces/${marketplaceId}/config`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async disconnectBrandMarketplace(brandId, marketplaceId) {
    return this.request(`/connector/brands/${brandId}/marketplaces/${marketplaceId}/disconnect`, {
      method: 'DELETE',
    });
  }

  /**
   * Removes the connection row itself, rather than switching it off.
   *
   * disconnectBrandMarketplace above only writes enabled:false — the connection stays in the
   * table and can be re-enabled. This one is gone: the server tears down the connection's
   * scheduled jobs first and refuses the delete if it cannot. Orders already collected are
   * keyed by brand and retailer, not by the connection, and are kept.
   */
  async deleteBrandMarketplace(brandId, marketplaceId) {
    return this.request(`/connector/brands/${brandId}/marketplaces/${marketplaceId}`, {
      method: 'DELETE',
    });
  }

  /** Queue a re-mirror of the brand's Shopify catalogue (shopify_variants). */
  async triggerCatalogueSync(brandId) {
    return this.request(`/connector/brands/${brandId}/catalogue-sync`, {
      method: 'POST',
    });
  }

  /**
   * The brand's mirrored Shopify locations, each marked with whether 2W ships from it.
   *
   * Reads shopify_locations, never Shopify: the brand page has to open when Shopify is slow,
   * and a store that has not re-approved the app since read_locations was added cannot serve
   * location names at all. `syncedAt` is null when the mirror has never run, which the tab
   * has to tell apart from a store that genuinely has one location.
   */
  async getShippingLocationOptions(brandId) {
    return this.request(`/connector/brands/${brandId}/shipping-location-options`);
  }

  /** Queue a re-mirror of the brand's Shopify locations (shopify_locations). */
  async triggerLocationSync(brandId) {
    return this.request(`/connector/brands/${brandId}/location-sync`, {
      method: 'POST',
    });
  }

  /** Row count, SKU-bearing count, and age of the brand's mirrored Shopify catalogue. */
  async getCatalogueStatus(brandId) {
    return this.request(`/connector/brands/${brandId}/catalogue-status`);
  }

  /** One page of the brand's mirrored Shopify products. */
  async getCatalogueProducts(brandId, options = {}) {
    const qs = new URLSearchParams(
      Object.entries(options).filter(([, v]) => v != null && v !== '')
    ).toString();
    return this.request(`/connector/brands/${brandId}/catalogue/products?${qs}`);
  }

  /** Variants — of one product when productId is given, otherwise the whole brand. */
  async getCatalogueVariants(brandId, options = {}) {
    const qs = new URLSearchParams(
      Object.entries(options).filter(([, v]) => v != null && v !== '')
    ).toString();
    return this.request(`/connector/brands/${brandId}/catalogue/variants?${qs}`);
  }

  async triggerBrandMarketplaceSync(brandId, marketplaceId) {
    return this.request(`/connector/brands/${brandId}/marketplaces/${marketplaceId}/sync`, {
      method: 'POST',
    });
  }

  async fetchBrandMarketplaceOrders(brandId, marketplaceId, startDate = null) {
    return this.request(`/connector/brands/${brandId}/marketplaces/${marketplaceId}/fetch-orders`, {
      method: 'POST',
      body: JSON.stringify({ startDate }),
    });
  }

  // ==========================================
  // EXPORT & UTILITY METHODS
  // ==========================================


  /**
   * Check API health and connectivity
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    try {
      // Simple health check that doesn't require auth
      const response = await fetch(`${this.baseUrl.replace('/admin', '')}/health`);
      return { status: 'ok', timestamp: new Date().toISOString() };
    } catch (error) {
      return { status: 'error', error: error.message, timestamp: new Date().toISOString() };
    }
  }

  /**
   * Get current admin user profile
   * @returns {Promise<Object>} User profile data
   */
  async getCurrentUser() {
    return this.request('/auth/profile');
  }

  /**
   * Refresh authentication token with secure handling
   * @returns {Promise<Object>} New token data
   */
  async refreshToken() {
    try {
      const currentToken = await secureAuthUtils.getToken();
      if (!currentToken) {
        throw new Error('No token available for refresh');
      }

      const response = await this.request('/auth/refresh', {
        method: 'POST',
        retry: false, // Don't retry token refresh to avoid loops
      });

      if (response.token) {
        // Update secure storage with new token
        const userData = await secureAuthUtils.getUserData();
        await this.setToken(response.token, userData);
        
        // Emit token refresh event
        window.dispatchEvent(new CustomEvent('token_refreshed', {
          detail: { timestamp: Date.now() }
        }));
      }

      return response;
    } catch (error) {
      console.error('Token refresh failed:', error);
      
      // If refresh fails, clear session and logout
      await this.clearToken();
      window.dispatchEvent(new CustomEvent('auth_failure', {
        detail: { reason: 'refresh_failed', error: error.message }
      }));
      
      throw error;
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Format currency for display
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (default: USD)
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  }

  /**
   * Format percentage for display
   * @param {number} rate - Rate as decimal (e.g., 0.30)
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted percentage string
   */
  formatPercentage(rate, decimals = 1) {
    return `${((rate || 0) * 100).toFixed(decimals)}%`;
  }

  /**
   * Format weight for display (pounds with kg conversion)
   * @param {number} pounds - Weight in pounds
   * @returns {string} Formatted weight string (e.g., "1.5 lb (0.68 kg)")
   */
  formatWeight(pounds) {
    if (!pounds || pounds === 0) return '0 lb (0 kg)';
    const kg = (pounds * 0.453592).toFixed(2);
    return `${pounds.toFixed(2)} lb (${kg} kg)`;
  }

  /**
   * Format date for display
   * @param {string|Date} date - Date to format
   * @param {string} format - Format type ('short', 'long', 'time')
   * @returns {string} Formatted date string
   */
  formatDate(date, format = 'short') {
    if (!date) return '';
    
    const dateObj = new Date(date);
    const options = {
      short: { year: 'numeric', month: 'short', day: 'numeric' },
      long: { year: 'numeric', month: 'long', day: 'numeric' },
      time: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
    };
    
    return dateObj.toLocaleDateString('en-US', options[format] || options.short);
  }

  /**
   * Generate query key for React Query caching
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Array} Query key array
   */
  getQueryKey(endpoint, params = {}) {
    return [endpoint, params];
  }

  /**
   * Calculate percentage change between two values
   * @param {number} current - Current value
   * @param {number} previous - Previous value
   * @returns {Object} Change data with percentage and direction
   */
  calculateChange(current, previous) {
    if (!previous || previous === 0) {
      return { percentage: current > 0 ? 100 : 0, direction: 'up', isNew: true };
    }

    const change = ((current - previous) / previous) * 100;
    return {
      percentage: Math.abs(change),
      direction: change >= 0 ? 'up' : 'down',
      isNew: false,
    };
  }

  // ==========================================
  // COMMISSION ADJUSTMENT METHODS
  // ==========================================

  /**
   * Get commission adjustments with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getCommissionAdjustments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/commission-adjustments${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      signal: params.signal,
    });
  }

  /**
   * Get pending commission adjustments for approval
   * @param {Object} options - Request options
   * @returns {Promise} API response
   */
  async getPendingAdjustments(options = {}) {
    return this.request('/commission-adjustments/pending', {
      signal: options.signal,
    });
  }

  /**
   * Create a new commission adjustment
   * @param {Object} adjustmentData - Adjustment data
   * @returns {Promise} API response
   */
  async createCommissionAdjustment(adjustmentData) {
    return this.request('/commission-adjustments', {
      method: 'POST',
      body: JSON.stringify(adjustmentData),
    });
  }

  /**
   * Update an existing commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @param {Object} updateData - Update data
   * @returns {Promise} API response
   */
  async updateCommissionAdjustment(adjustmentId, updateData) {
    return this.request(`/commission-adjustments/${adjustmentId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  /**
   * Approve a commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @returns {Promise} API response
   */
  async approveCommissionAdjustment(adjustmentId) {
    return this.request(`/commission-adjustments/${adjustmentId}/approve`, {
      method: 'POST',
    });
  }

  /**
   * Reject a commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @param {string} reason - Rejection reason
   * @returns {Promise} API response
   */
  async rejectCommissionAdjustment(adjustmentId, reason = '') {
    return this.request(`/commission-adjustments/${adjustmentId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Delete a commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @param {string} reason - Deletion reason
   * @returns {Promise} API response
   */
  async deleteCommissionAdjustment(adjustmentId, reason = '') {
    return this.request(`/commission-adjustments/${adjustmentId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Get audit trail for an adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @returns {Promise} API response
   */
  async getAdjustmentAuditTrail(adjustmentId) {
    return this.request(`/commission-adjustments/${adjustmentId}/audit`);
  }

  /**
   * Calculate adjustment impact before creating
   * @param {Object} impactData - Impact calculation data
   * @returns {Promise} API response
   */
  async calculateAdjustmentImpact(impactData) {
    return this.request('/commission-adjustments/calculate-impact', {
      method: 'POST',
      body: JSON.stringify(impactData),
    });
  }

  // ============================================
  // INVOICE MANAGEMENT METHODS
  // ============================================

  /**
   * Get invoice settings for a brand
   * @param {string} brandId - Brand ID
   * @returns {Promise} API response
   */
  async getInvoiceSettings(brandId) {
    return this.request(`/brands/${brandId}/invoice-settings`);
  }

  /**
   * Update invoice settings for a brand
   * @param {string} brandId - Brand ID
   * @param {Object} settings - Invoice settings
   * @returns {Promise} API response
   */
  async updateInvoiceSettings(brandId, settings) {
    return this.request(`/brands/${brandId}/invoice-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  /**
   * Get all invoices with filters
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getInvoices(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoices${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get single invoice by ID
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async getInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}`);
  }

  /**
   * Trigger monthly invoice generation
   * Uses optimized path that reads from BillingReport (no recalculation)
   * @param {Object} data
   * @param {string} [data.monthString] - 'YYYY-MM'. Defaults to the previous month, and is
   *        required when brandId is given.
   * @param {string} [data.brandId] - Limits the run to one brand. Omitted, every brand with
   *        complete invoice settings is considered. An invoice is never replaced: naming a
   *        brand whose month is already invoiced is refused with a 400, and the all-brands
   *        run silently skips such a brand.
   * @returns {Promise} API response
   */
  async triggerMonthlyInvoices(data = {}) {
    return this.request('/invoices/trigger-monthly', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Generate invoice from billing report (optimized - single brand)
   * @param {Object} data - { brandId, monthString }
   * @returns {Promise} API response
   */
  async generateInvoiceFromReport(data) {
    return this.request('/invoices/generate-from-report', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Approve invoice
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async approveInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}/approve`, {
      method: 'PUT',
    });
  }

  /**
   * Send invoice email
   * @param {string} invoiceId - Invoice ID
   * @param {string[]} recipients - Email recipients (optional)
   * @returns {Promise} API response
   */
  async sendInvoice(invoiceId, recipients = null) {
    return this.request(`/invoices/${invoiceId}/send`, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    });
  }

  /**
   * Record payment for invoice
   * @param {string} invoiceId - Invoice ID
   * @param {Object} paymentData - Payment details
   * @returns {Promise} API response
   */
  async recordInvoicePayment(invoiceId, paymentData) {
    return this.request(`/invoices/${invoiceId}/payment`, {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }

  /**
   * Cancel invoice
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async cancelInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}/cancel`, {
      method: 'PUT',
    });
  }

  /**
   * Delete invoice (only DRAFT or CANCELLED invoices)
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async deleteInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Regenerate invoice (only DRAFT invoices)
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response with updated invoice
   */
  async regenerateInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}/regenerate`, {
      method: 'POST',
    });
  }

  /**
   * Get invoice PDF download URL
   * @param {string} invoiceId - Invoice ID
   * @returns {string} PDF download URL
   */
  getInvoicePdfUrl(invoiceId) {
    return `${this.baseUrl}/invoices/${invoiceId}/pdf`;
  }

  /**
   * Download invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} Blob response
   */
  /**
   * The SKU comparison as a CSV, with the table's own filters.
   *
   * Returns a blob rather than parsed JSON: the server sends text/csv and the browser saves
   * it, so nothing here needs to read it.
   */
  async exportSkuMap(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ).toString();
    return this.downloadBlob(`/connector/sku-map/export${qs ? `?${qs}` : ''}`);
  }

  /** One retailer's catalogue as a CSV, with the table's own filters. */
  async exportRetailerCatalogue(marketplaceId, params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ).toString();
    return this.downloadBlob(`/connector/marketplaces/${marketplaceId}/catalogue/export${qs ? `?${qs}` : ''}`);
  }

  /** A response the browser saves rather than one this client parses. */
  async downloadBlob(path) {
    await this.ensureToken();
    const response = await fetch(`${this.baseUrl}${path}`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`);
    }
    return response.blob();
  }

  async downloadInvoicePdf(invoiceId) {
    await this.ensureToken();
    const response = await fetch(`${this.baseUrl}/invoices/${invoiceId}/pdf`, {
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
    });
    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }
    return response.blob();
  }

  /**
   * Regenerate invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async regenerateInvoicePdf(invoiceId) {
    return this.request(`/invoices/${invoiceId}/regenerate-pdf`, {
      method: 'POST',
    });
  }

  /**
   * Verify email configuration
   * @returns {Promise} API response
   */
  async verifyEmailConfig() {
    return this.request('/email/verify');
  }

  /**
   * Send test email
   * @param {string} to - Email address
   * @returns {Promise} API response
   */
  async sendTestEmail(to) {
    return this.request('/email/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    });
  }
}

export const apiClient = new ApiClient();
export default apiClient;